import { useCallback, useEffect, useRef } from 'react'

export function useRafLoop(callback: () => void, initiallyActive = true): [() => void, () => void, () => boolean] {
  const frameRef = useRef<number | null>(null)
  const callbackRef = useRef(callback)
  const loopRef = useRef<() => void>(() => undefined)
  const isActiveRef = useRef(false)

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    loopRef.current = () => {
      if (!isActiveRef.current) return

      callbackRef.current()
      frameRef.current = window.requestAnimationFrame(loopRef.current)
    }
  }, [])

  const stop = useCallback(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }

    isActiveRef.current = false
  }, [])

  const start = useCallback(() => {
    if (isActiveRef.current || typeof window === 'undefined') return

    isActiveRef.current = true
    frameRef.current = window.requestAnimationFrame(loopRef.current)
  }, [])

  const isActive = useCallback(() => isActiveRef.current, [])

  useEffect(() => {
    if (initiallyActive) {
      start()
    }

    return stop
  }, [initiallyActive, start, stop])

  return [stop, start, isActive]
}
